import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "@/lib/api";

const CATEGORY_LABELS = {
  reconciliation: "Reconciliation",
  management: "Management",
  audit: "Governance",
};
const CATEGORY_ORDER = ["reconciliation", "management", "audit"];

const HINT = {
  executive_summary: "Controller-to-CFO monthly decision document. Six sections: reconciled, unresolved, materiality, attention, risk, decisions.",
  audit_trail: "Cross-period filterable activity log. Default scope: last 90 days.",
  governance_exceptions: "Blockers, overrides, skips, and exclusions captured during the close.",
  recognition_activity: "Per-run Recognition Engine emissions and dispositions; flags high-confidence rejects and low-confidence overrides.",
  reconciliation_summary: "One-page balance + variance + blockers for the selected period.",
  outstanding_items: "Every under_review transaction in the selected period.",
  matchgroup_report: "All match groups for the period with signals and rationale.",
  journal_entry_report: "CAJE / BSJE / DRJE list for the period.",
  variance_analysis: "Variance breakdown by category with contributing items.",
  unmatched_reconciliation_detail: "Every unmatched bank line and every unmatched GL line, sorted by amount, with running cumulative net — use to identify which items are driving the GL variance so they can be matched, journalized, or recorded as reconciling items.",
  certification_package: "Multi-period certification package — the controller deliverable for a quarter.",
};

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function buildReportUrl(reportId, fmt, payload) {
  // P0 · Feb 2026 — direct-GET, no blob.
  // The Certification Package uses a short benign URL identical in shape
  // to the proven-working /api/periods/<id>/binder.pdf path so that
  // browser ad-blockers / privacy filters that previously matched
  // "certification_package" (the word "package") can no longer intercept
  // the request (`ERR_BLOCKED_BY_CLIENT`).
  const base = process.env.REACT_APP_BACKEND_URL;
  if (reportId === "certification_package" && fmt === "pdf") {
    const ids = Array.isArray(payload.period_ids)
      ? payload.period_ids
      : (payload.period_ids ? [payload.period_ids] : []);
    if (ids.length === 1) {
      return `${base}/api/periods/${encodeURIComponent(ids[0])}/cert.pdf`;
    }
  }
  // Fallback: generic reports orchestrator URL with query params.
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(payload)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach(item => qs.append(k, item));
    else qs.append(k, v);
  }
  const tail = qs.toString();
  return `${base}/api/reports/${reportId}.${fmt}${tail ? `?${tail}` : ""}`;
}

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [runs, setRuns] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [format, setFormat] = useState("pdf");
  const [params, setParams] = useState({});
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const active = reports.find(r => r.id === activeId);

  useEffect(() => {
    Promise.all([
      api.listReports(),
      api.periods({}),
      api.listReportRuns(20),
    ]).then(([r, p, runsResp]) => {
      setReports(r.reports || []);
      setPeriods(p || []);
      setRuns(runsResp.runs || []);
      if (r.reports?.length) {
        setActiveId(r.reports[0].id);
      }
      setLoading(false);
    }).catch(err => {
      setError(err.message || "Failed to load reports");
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    setFormat(active.formats.includes("pdf") ? "pdf" : "csv");
    setParams({});
    setError("");
  }, [activeId]);

  const grouped = useMemo(() => {
    const m = {};
    for (const r of reports) {
      const cat = r.category || "reconciliation";
      (m[cat] ||= []).push(r);
    }
    return m;
  }, [reports]);

  const requiredMissing = useMemo(() => {
    if (!active) return [];
    return (active.params || []).filter(p => {
      const v = params[p];
      if (Array.isArray(v)) return v.length === 0;
      return v === undefined || v === null || v === "";
    });
  }, [active, params]);

  const setParam = (k, v) => setParams(prev => ({ ...prev, [k]: v }));

  async function handleRun() {
    if (!active) return;
    if (requiredMissing.length) {
      setError(`Missing required parameter(s): ${requiredMissing.join(", ")}`);
      return;
    }
    setRunning(true);
    setError("");
    try {
      const payload = {};
      for (const k of [...(active.params || []), ...(active.optional_params || [])]) {
        const v = params[k];
        if (v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)) {
          payload[k] = v;
        }
      }
      // Direct-GET to the short benign URL. Cert Package goes via
      // /api/periods/{id}/cert.pdf (same shape as the proven-working
      // /binder.pdf URL). All other reports use the standard orchestrator.
      const url = buildReportUrl(active.id, format, payload);
      window.open(url, "_blank", "noopener,noreferrer");
      // Refresh the runs table after a moment so the new sha-256 + size show.
      setTimeout(async () => {
        try {
          const runsResp = await api.listReportRuns(20);
          setRuns(runsResp.runs || []);
        } catch { /* non-fatal */ }
      }, 1500);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Run failed";
      setError(msg);
    } finally {
      setRunning(false);
    }
  }

  if (loading) {
    return <div className="loading" data-testid="reports-loading">Loading reports…</div>;
  }

  // Build a `report_id -> display label` lookup so the Recent-runs table
  // shows the same Controller-facing names as the left-rail catalogue.
  const reportLabels = {};
  for (const r of reports || []) reportLabels[r.id] = r.label;
  const labelFor = (rid) => reportLabels[rid] || rid;

  return (
    <div data-testid="reports-page">
      <header style={{ marginBottom: 18 }}>
        <div className="heading h1" data-testid="reports-title">Reports</div>
        <div className="muted" style={{ marginTop: 4, fontSize: 14 }}>
          Every report supports a decision. Reports are governance-neutral — running one never mutates reconciliation data.
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 320px) 1fr", gap: 24 }}>
        {/* ── Left: catalogue ───────────────────────────────────────────── */}
        <aside data-testid="reports-catalogue" style={{
          background: "var(--surface)", border: "1px solid var(--line)",
          borderRadius: 8, padding: "14px 0", height: "fit-content",
        }}>
          {CATEGORY_ORDER.map(cat => {
            const items = grouped[cat] || [];
            if (!items.length) return null;
            return (
              <div key={cat} style={{ marginBottom: 10 }}>
                <div className="nav-section" style={{ padding: "4px 18px" }}>
                  {CATEGORY_LABELS[cat] || cat}
                </div>
                {items.map(r => {
                  const isActive = r.id === activeId;
                  return (
                    <button
                      key={r.id}
                      onClick={() => setActiveId(r.id)}
                      data-testid={`report-item-${r.id}`}
                      style={{
                        display: "block", width: "100%", textAlign: "left",
                        padding: "8px 18px",
                        background: isActive ? "var(--secondary)" : "transparent",
                        border: "none",
                        borderLeft: isActive ? "3px solid var(--primary)" : "3px solid transparent",
                        cursor: "pointer", fontSize: 13,
                        color: isActive ? "var(--accent)" : "var(--ink)",
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      <div>{r.label}</div>
                      <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                        {r.formats.join(" / ")}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </aside>

        {/* ── Right: configure + run + history ──────────────────────────── */}
        <section>
          {active && (
            <div data-testid="report-configure" style={{
              background: "var(--surface)", border: "1px solid var(--line)",
              borderRadius: 8, padding: 24, marginBottom: 24,
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <div className="heading h2" data-testid="report-title">{active.label}</div>
                  <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                    {HINT[active.id] || active.description || ""}
                  </div>
                </div>
              </div>

              <ReportParamForm
                report={active}
                params={params}
                setParam={setParam}
                periods={periods}
              />

              <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 18 }}>
                <label style={{ fontSize: 13 }}>
                  Format:&nbsp;
                  <select
                    value={format}
                    onChange={e => setFormat(e.target.value)}
                    data-testid="report-format"
                  >
                    {active.formats.map(f => (
                      <option key={f} value={f}>{f.toUpperCase()}</option>
                    ))}
                  </select>
                </label>
                <button
                  className="btn primary"
                  onClick={handleRun}
                  disabled={running || requiredMissing.length > 0}
                  data-testid="report-run-btn"
                >
                  {running ? "Generating…" : `Generate ${format.toUpperCase()}`}
                </button>
                {requiredMissing.length > 0 && (
                  <span className="muted" style={{ fontSize: 12 }}>
                    Missing: {requiredMissing.join(", ")}
                  </span>
                )}
              </div>

              {error && (
                <div data-testid="report-error" style={{
                  marginTop: 14, padding: "8px 12px",
                  background: "#fff4f0", border: "1px solid #f3c2b3",
                  color: "#8a2a0c", borderRadius: 6, fontSize: 13,
                }}>
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Recent runs */}
          <div data-testid="report-runs" style={{
            background: "var(--surface)", border: "1px solid var(--line)",
            borderRadius: 8, padding: 20,
          }}>
            <div className="heading h2" style={{ fontSize: 16, marginBottom: 10 }}>
              Recent runs
            </div>
            {runs.length === 0 ? (
              <div className="muted" style={{ fontSize: 13 }}>
                No reports have been run yet.
              </div>
            ) : (
              <table className="table" style={{ width: "100%", fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}>Report</th>
                    <th>Format</th>
                    <th>Size</th>
                    <th>By</th>
                    <th>When</th>
                    <th>SHA-256</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(r => (
                    <tr key={r.id} data-testid={`run-row-${r.id}`}>
                      <td>{labelFor(r.report_id)}</td>
                      <td style={{ textAlign: "center" }}>{(r.format || "").toUpperCase()}</td>
                      <td style={{ textAlign: "right" }}>{formatBytes(r.byte_size)}</td>
                      <td>{r.requested_by}</td>
                      <td>{fmt.dateTime(r.created_at)}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 11 }}>
                        {(r.sha256 || "").slice(0, 12)}…
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function ReportParamForm({ report, params, setParam, periods }) {
  const required = report.params || [];
  const optional = report.optional_params || [];

  if (required.length === 0 && optional.length === 0) {
    return (
      <div className="muted" style={{ fontSize: 13 }}>
        No parameters required — this report draws from the entire dataset.
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {required.map(p => (
        <ParamField
          key={p} name={p} required
          report={report} params={params} setParam={setParam}
          periods={periods}
        />
      ))}
      {optional.length > 0 && (
        <details style={{ fontSize: 13 }}>
          <summary style={{ cursor: "pointer", padding: "4px 0" }}>
            Optional filters ({optional.length})
          </summary>
          <div style={{ display: "grid", gap: 12, marginTop: 8 }}>
            {optional.map(p => (
              <ParamField
                key={p} name={p}
                report={report} params={params} setParam={setParam}
                periods={periods}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function ParamField({ name, required, params, setParam, periods }) {
  const val = params[name];
  const testid = `param-${name}`;

  if (name === "period_id") {
    return (
      <Label name="Period" required={required}>
        <select
          value={val || ""}
          onChange={e => setParam(name, e.target.value)}
          data-testid={testid}
          style={{ width: "100%" }}
        >
          <option value="">— Select period —</option>
          {periods.map(p => (
            <option key={p.id} value={p.id}>
              {p.period_label} · {p.status}
            </option>
          ))}
        </select>
      </Label>
    );
  }
  if (name === "period_ids") {
    const arr = Array.isArray(val) ? val : [];
    return (
      <Label name="Periods (multi-select)" required={required}>
        <select
          multiple
          value={arr}
          onChange={e => {
            const v = Array.from(e.target.selectedOptions).map(o => o.value);
            setParam(name, v);
          }}
          data-testid={testid}
          style={{ width: "100%", minHeight: 120 }}
        >
          {periods.map(p => (
            <option key={p.id} value={p.id}>
              {p.period_label} · {p.status}
            </option>
          ))}
        </select>
        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          Hold Ctrl / Cmd to select multiple periods.
        </div>
      </Label>
    );
  }
  if (name === "date_from" || name === "date_to") {
    return (
      <Label name={name === "date_from" ? "From" : "To"} required={required}>
        <input
          type="date"
          value={val || ""}
          onChange={e => setParam(name, e.target.value ? `${e.target.value}T00:00:00Z` : "")}
          data-testid={testid}
        />
      </Label>
    );
  }
  if (name === "limit") {
    return (
      <Label name="Limit" required={required}>
        <input
          type="number" min={1} max={5000}
          value={val || ""}
          placeholder="2000"
          onChange={e => setParam(name, e.target.value ? Number(e.target.value) : null)}
          data-testid={testid}
        />
      </Label>
    );
  }
  return (
    <Label name={name.replace(/_/g, " ")} required={required}>
      <input
        type="text"
        value={val || ""}
        onChange={e => setParam(name, e.target.value)}
        data-testid={testid}
        style={{ width: "100%" }}
      />
    </Label>
  );
}

function Label({ name, required, children }) {
  return (
    <label style={{ display: "block", fontSize: 13 }}>
      <div style={{ marginBottom: 4, fontWeight: 500, textTransform: "capitalize" }}>
        {name}{required ? " *" : ""}
      </div>
      {children}
    </label>
  );
}
